/**
 * Optional preference attrs for item requests (D12 / Phase 2.1).
 * Persist as flat JSON (listing attributes + brand/model/size).
 * Matching uses soft boosts only — never hard-excludes.
 */

import { buildListingAttributesForSubmit } from "@/lib/listing/listingSpecPlugin";
import {
  emptyFamilySpecs,
  type ListingFamilySpecs,
} from "@/lib/listing/listingFamilyRuntime";

export type PreferenceAttrValue = string | number | boolean;
export type PreferenceAttrs = Record<string, PreferenceAttrValue>;

export type PreferenceFit = "unset" | "full" | "partial" | "none";

export const PREFERENCE_ATTR_SCORE_PER_KEY = 8;
export const PREFERENCE_ATTR_SCORE_CAP = 24;
export const PREFERENCE_ATTRS_MAX_KEYS = 24;
const STRING_MAX_LEN = 80;

const GENERIC_KEYS = new Set(["brand", "model", "size"]);

/** Enum / coded values → plain language for demand strip + capture chrome. */
const PREF_VALUE_LABELS: Record<string, string> = {
  front_loader: "front loader",
  top_loader: "top loader",
  inverter: "inverter",
  non_inverter: "non-inverter",
  non_inv: "non-inverter",
  l_shaped: "L-shaped",
  straight: "straight",
};

function isPlainRecord(value: unknown): value is Record<string, unknown> {
  return value != null && typeof value === "object" && !Array.isArray(value);
}

function normalizeScalar(value: unknown): PreferenceAttrValue | undefined {
  if (typeof value === "boolean") return value;
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return undefined;
    return trimmed.slice(0, STRING_MAX_LEN);
  }
  return undefined;
}

/**
 * Format size for display without doubling units (e.g. "50 In" → "~50″", not "~50 In″").
 */
export function formatPreferenceSizeLabel(size: string): string {
  const s = size.trim();
  if (!s) return "";
  if (/[″"]/.test(s)) return s.startsWith("~") ? s : `~${s.replace(/"/g, "″")}`;
  const inches = s.match(/^(\d+(?:\.\d+)?)\s*(in|inch|inches)?\.?$/i);
  if (inches) return `~${inches[1]}″`;
  if (/^\d+(?:\.\d+)?\s*L$/i.test(s)) return s.replace(/\s+/g, "");
  if (/kva/i.test(s)) return s.replace(/\s+/g, "");
  // Has letters (e.g. model-ish) — leave as-is, no forced inch mark
  if (/[a-zA-Z]/.test(s)) return s;
  return `~${s}″`;
}

function humanizePrefValue(key: string, value: PreferenceAttrValue): string {
  if (key === "horse_power" || key === "hp") {
    return `${value} HP`;
  }
  if (key === "load_capacity_kg") {
    return `${value} kg`;
  }
  if (typeof value === "string") {
    const mapped =
      PREF_VALUE_LABELS[value] ?? PREF_VALUE_LABELS[value.toLowerCase()];
    if (mapped) return mapped;
    if (value.includes("_")) return value.replace(/_/g, " ");
    return value;
  }
  if (typeof value === "boolean") {
    return value ? key.replace(/_/g, " ") : "";
  }
  return String(value);
}

/**
 * Sanitize arbitrary JSON into a flat preference map.
 * Drops empties, arrays, nested objects; caps key count.
 */
export function normalizePreferenceAttrs(raw: unknown): PreferenceAttrs {
  if (!isPlainRecord(raw)) return {};
  const next: PreferenceAttrs = {};
  for (const [key, value] of Object.entries(raw)) {
    if (!key || key.length > 64) continue;
    const normalized = normalizeScalar(value);
    if (normalized === undefined) continue;
    next[key] = normalized;
    if (Object.keys(next).length >= PREFERENCE_ATTRS_MAX_KEYS) break;
  }
  return next;
}

export function preferenceAttrsAreEmpty(attrs: PreferenceAttrs): boolean {
  return Object.keys(attrs).length === 0;
}

/**
 * Short human summary for capture step chrome + demand strip (e.g. "prefer LG · ~55″").
 * Empty prefs → "".
 */
export function formatPreferenceSummary(attrs: PreferenceAttrs): string {
  if (preferenceAttrsAreEmpty(attrs)) return "";
  const parts: string[] = [];
  const brand = attrs.brand;
  if (typeof brand === "string" && brand.trim()) {
    parts.push(`prefer ${brand.trim()}`);
  }
  const size = attrs.size;
  if (typeof size === "string" && size.trim()) {
    parts.push(formatPreferenceSizeLabel(size));
  } else if (typeof attrs.screen_inches === "number") {
    parts.push(`~${attrs.screen_inches}″`);
  } else if (typeof attrs.screen_inches === "string" && attrs.screen_inches) {
    parts.push(formatPreferenceSizeLabel(String(attrs.screen_inches)));
  }
  const model = attrs.model;
  if (typeof model === "string" && model.trim()) {
    parts.push(model.trim());
  }
  // A couple of non-generic keys if brand/size/model didn't fill enough
  if (parts.length < 2) {
    for (const [key, value] of Object.entries(attrs)) {
      if (GENERIC_KEYS.has(key) || key === "screen_inches") continue;
      const label = humanizePrefValue(key, value);
      if (!label) continue;
      parts.push(label);
      if (parts.length >= 3) break;
    }
  }
  return parts.join(" · ");
}

/**
 * Build preference attrs from capture form (family specs + optional brand/size/model).
 * Empty form → {}.
 */
export function buildPreferenceAttrsFromCapture(input: {
  subcategoryId: string;
  brand?: string;
  size?: string;
  model?: string;
  familySpecs?: ListingFamilySpecs | null;
}): PreferenceAttrs {
  const familySpecs = input.familySpecs ?? emptyFamilySpecs();
  const fromFamily = buildListingAttributesForSubmit({
    subcategoryId: input.subcategoryId,
    sizeText: input.size ?? "",
    familySpecs,
  });

  const merged: Record<string, unknown> = { ...fromFamily };
  const brand = (input.brand || "").trim();
  const model = (input.model || "").trim();
  const size = (input.size || "").trim();
  if (brand) merged.brand = brand;
  if (model) merged.model = model;
  // Keep human size string when present; family may also set screen_inches etc.
  if (size) merged.size = size;

  return normalizePreferenceAttrs(merged);
}

function valuesAgree(
  preferred: PreferenceAttrValue,
  actual: PreferenceAttrValue,
): boolean {
  if (typeof preferred === "number" && typeof actual === "number") {
    return preferred === actual;
  }
  if (typeof preferred === "boolean" && typeof actual === "boolean") {
    return preferred === actual;
  }
  const a = String(preferred).trim().toLowerCase();
  const b = String(actual).trim().toLowerCase();
  if (!a || !b) return false;
  return a === b || a.includes(b) || b.includes(a);
}

/**
 * Merge listing attributes jsonb with brand/model columns for comparison.
 * Size is attributes-only (no `items.size` column).
 */
export function listingAttrsForPreferenceCompare(input: {
  attributes?: unknown;
  brand?: string | null;
  size?: string | null;
  model?: string | null;
}): PreferenceAttrs {
  const fromJson = normalizePreferenceAttrs(input.attributes ?? {});
  const merged: PreferenceAttrs = { ...fromJson };
  const brand = (input.brand || "").trim();
  const size = (input.size || "").trim();
  const model = (input.model || "").trim();
  if (brand) merged.brand = brand;
  if (size) merged.size = size;
  if (model) merged.model = model;
  return merged;
}

export type PreferenceCompareResult = {
  fit: PreferenceFit;
  keysCompared: number;
  keysMatched: number;
  /** Soft score contribution (0…PREF_SCORE_CAP) */
  scoreBoost: number;
};

/**
 * Soft preference compare — never excludes; empty prefs → unset / 0 boost.
 */
export function comparePreferenceAttrs(
  preferred: PreferenceAttrs | null | undefined,
  listing: PreferenceAttrs,
): PreferenceCompareResult {
  const prefs = normalizePreferenceAttrs(preferred ?? {});
  const keys = Object.keys(prefs);
  if (keys.length === 0) {
    return { fit: "unset", keysCompared: 0, keysMatched: 0, scoreBoost: 0 };
  }

  let matched = 0;
  for (const key of keys) {
    const want = prefs[key];
    const got = listing[key];
    if (want === undefined || got === undefined) continue;
    if (valuesAgree(want, got)) matched += 1;
  }

  const scoreBoost = Math.min(
    PREFERENCE_ATTR_SCORE_CAP,
    matched * PREFERENCE_ATTR_SCORE_PER_KEY,
  );

  let fit: PreferenceFit;
  if (matched === 0) fit = "none";
  else if (matched >= keys.length) fit = "full";
  else fit = "partial";

  return {
    fit,
    keysCompared: keys.length,
    keysMatched: matched,
    scoreBoost,
  };
}

/** Pull-list copy helper (U2). */
export function preferenceFitLabel(fit: PreferenceFit): string | null {
  switch (fit) {
    case "full":
      return "Matches your prefs";
    case "partial":
      return "Some prefs match";
    case "none":
      return "Same type · different details";
    default:
      return null;
  }
}

export function isGenericPreferenceKey(key: string): boolean {
  return GENERIC_KEYS.has(key);
}
